/* eslint-disable no-param-reassign */
import {
  Swiper,
  Navigation,
  Pagination,
  Lazy,
} from 'swiper';
import { breakpoints } from 'js/config/breakpoints';

class SyndicatedVenuesGallery {
  constructor() {
    this.changerType = '';
    this.changerElement = '';
    this.swiper = '';
    this.swiperBlock = '';
    this.venueName = '';
    this.blockClass = 'venues-gallery';
  }

  setActiveElement(element) {
    this.changerElement = element;

    if (this.changerType === 'tabs') {
      const items = element.closest(`.${this.blockClass}`).querySelectorAll(`.${this.blockClass}__list-item`);
      const activeClass = `${this.blockClass}__list-item--active`;
      const parentElement = this.changerElement.closest(`.${this.blockClass}__list`);
      const activedItem = parentElement.querySelector(`.${activeClass}`);

      items?.forEach((item) => {
        item.ariaPressed = false;
      });

      activedItem.classList.remove(activeClass);
      this.changerElement.classList.add(activeClass);
      this.changerElement.ariaPressed = true;
    }

    if (element.tagName === 'BUTTON' || element.tagName === 'LI') {
      this.venueName = element.textContent.trim();
    }

    if (this.venueName) {
      if (this.changerType === 'switcher') {
        element.closest(`.${this.blockClass}__switcher`)
          .querySelector(`.${this.blockClass}__switcher-value`)
          .textContent = this.venueName;

        this.swiperBlock.closest('.venues-gallery').querySelector('.venues-gallery__switcher-inner').focus();
      }

      this.disablePreviousSlider(element);
      this.swiperBlock = element.closest(`.${this.blockClass}`).querySelector(`.${this.blockClass}__venue[data-title="${this.venueName}"]`);
      this.activateSlider();
    }

    this.venueName = '';
  }

  disablePreviousSlider = (element) => {
    const previousSwiperBlock = Array.from(element.closest(`.${this.blockClass}`).querySelectorAll(`.${this.blockClass}__venue .swiper-container`))
      .find((block) => block.swiper !== undefined);

    const previousActivatedImages = Array.from(element.closest(`.${this.blockClass}`).querySelectorAll(`.${this.blockClass}__venue .${this.blockClass}__slide--single`))
      .find((block) => block.classList.contains('active'));

    if (previousSwiperBlock) {
      previousSwiperBlock.swiper.destroy();
      previousSwiperBlock.swiper = undefined;
    }

    if (previousActivatedImages) {
      previousActivatedImages.classList.remove('active');
    }
  };

  activateSlider = () => {
    if (this.swiperBlock.querySelector('.swiper-container')) {
      this.swiperBlock = this.swiperBlock.querySelector('.swiper-container');
      this.initSwiper();
    } else {
      const slide = this.swiperBlock.querySelector('.venues-gallery__slide');
      if (slide) {
        slide.classList.add('active');
        this.hidePreloaderForSingleImage(slide);
      }
    }
  };

  hidePreloaderForSingleImage = (slide) => {
    const image = slide.querySelector(`.${this.blockClass}__image`);
    const preloader = slide.querySelector('.swiper__preloader');

    if (image && preloader) {
      if (image.complete && image.naturalHeight !== 0) {
        preloader.style.display = 'none';
      } else {
        image.addEventListener('load', () => {
          setTimeout(() => {
            preloader.style.display = 'none';
          }, 0);
        }, { once: true });
      }
    }
  };

  initSwiper = () => {
    const venueSlider = this.swiperBlock;
    const venueBlock = venueSlider.closest(`.${this.blockClass}__venue`);
    let { swiper } = venueSlider;

    if (venueBlock.classList.contains('container')) {
      return;
    }

    if (swiper && typeof swiper.destroy === 'function') {
      swiper.destroy();
      swiper = null;
    }

    swiper = new Swiper(venueSlider, {
      slidesPerView: 'auto',
      spaceBetween: 24,
      centeredSlides: false,
      modules: [Navigation, Pagination, Lazy],
      lazy: {
        preloaderClass: 'swiper__preloader',
      },
      navigation: {
        nextEl: venueBlock.querySelector('.swiper-button-next'),
        prevEl: venueBlock.querySelector('.swiper-button-prev'),
      },
      pagination: {
        el: venueBlock.querySelector('.swiper-pagination'),
        clickable: true,
        renderBullet: (index, className) => `<button type="button" class="${className}" aria-label="Slide ${index + 1}"></button>`,
      },
      watchSlidesProgress: true,
      watchOverflow: true,
      loop: true,
    });
  };

  activateSwitcher = () => {
    const switchers = document.querySelectorAll(`.${this.blockClass}__switcher-inner`);
    const switchersItems = document.querySelectorAll(`.${this.blockClass}__switcher-list li`);

    switchers.forEach((switcher) => {
      switcher.addEventListener('click', () => {
        this.setActiveElement(switcher);
      });

      switcher.addEventListener('keyup', (event) => {
        if (event.keyCode === 13) {
          this.setActiveElement(switcher);
        }
      });
    });

    switchersItems.forEach((switcherItem) => {
      switcherItem.addEventListener('click', () => {
        this.setActiveElement(switcherItem);
      });

      switcherItem.addEventListener('keyup', (event) => {
        if (event.keyCode === 13) {
          this.setActiveElement(switcherItem);
        }
      });
    });
  };

  toggleTabsSwitcher = (blockContainer) => {
    const tabs = blockContainer.querySelector(`.${this.blockClass}__list`);
    const switcher = blockContainer.querySelector(`.${this.blockClass}__switcher`);

    if (tabs.offsetHeight === 0) {
      return;
    }

    if ((tabs && tabs.offsetHeight > 24) || window.screen.width < breakpoints.lg) {
      this.activateSwitcher();
      tabs.classList.add(`${this.blockClass}__list--hidden`);
      switcher.classList.remove(`${this.blockClass}__switcher--hidden`);
      this.changerType = 'switcher';
    } else {
      switcher.classList.add(`${this.blockClass}__switcher--hidden`);
      tabs.classList.remove(`${this.blockClass}__list--hidden`);
      this.changerType = 'tabs';
    }
  };

  clickOnTab = (event) => {
    if (event.target.classList.contains(`${this.blockClass}__list-item`) && this.changerType === 'tabs') {
      event.preventDefault();
      this.setActiveElement(event.target);
    }
  };

  static supportAdaBullets = (event) => {
    const { target } = event;

    if (target.classList.contains('swiper-button') || target.classList.contains('swiper-pagination-bullet')) {
      target.click();

      if (target.classList.contains('swiper-button-disabled')) {
        const swiperControlsElement = target.closest('.swiper-controls');
        if (swiperControlsElement) {
          const swiperBullet = swiperControlsElement.querySelector(
            target.classList.contains('swiper-button-next')
              ? '.swiper-pagination-bullet:last-child'
              : '.swiper-pagination-bullet:first-child',
          );
          if (swiperBullet) {
            swiperBullet.focus();
          }
        }
      }
    }
  };

  supportAdaListItems = (event) => {
    const { target } = event;
    if (target.classList.contains(`${this.blockClass}__list-item`)) {
      this.setActiveElement(target);
    }
  };

  static supportAdaArrows = (event) => {
    const { target } = event;
    if (target.classList.contains('swiper-button-prev') || target.classList.contains('swiper-button-next')) {
      target.click();
    }
  };

  supportSwitcher = (event) => {
    const { target } = event;
    if (
      target.classList.contains(`${this.blockClass}__switcher-value`)
      || target.classList.contains(`${this.blockClass}__switcher-inner`)
      || target.closest(`.${this.blockClass}__switcher`)
    ) {
      const parentElement = this.changerElement.closest(`.${this.blockClass}__switcher`);
      parentElement.classList.toggle(`${this.blockClass}__switcher--open`);
    }
  };

  initToggleTabsSwitcher = () => {
    const blockContainers = document.querySelectorAll(`section.${this.blockClass}`);
    blockContainers.forEach((blockContainer) => {
      this.toggleTabsSwitcher(blockContainer);
    });
  };

  init = () => {
    const blockContainers = document.querySelectorAll(`section.${this.blockClass}`);

    blockContainers.forEach((blockContainer) => {
      this.swiperBlock = blockContainer.querySelector(`.${this.blockClass}__venue`);
      this.activateSlider();

      const activeSlide = blockContainer.querySelector(`.${this.blockClass}__slide--single.active`);
      if (activeSlide) {
        this.hidePreloaderForSingleImage(activeSlide);
      }
    });

    this.initToggleTabsSwitcher(blockContainers);
  };
}

const SyndicatedVenuesGalleryObject = new SyndicatedVenuesGallery();

document.addEventListener('DOMContentLoaded', () => {
  SyndicatedVenuesGalleryObject.init();
});

window.addEventListener('resize', () => {
  SyndicatedVenuesGalleryObject.initToggleTabsSwitcher();
});

document.addEventListener('keyup', (event) => {
  if (event.keyCode === 13) {
    SyndicatedVenuesGallery.supportAdaBullets(event);
    SyndicatedVenuesGallery.supportAdaArrows(event);
    SyndicatedVenuesGalleryObject.supportAdaListItems(event);
    SyndicatedVenuesGalleryObject.supportSwitcher(event);
  }
});

document.addEventListener('click', (event) => {
  SyndicatedVenuesGalleryObject.clickOnTab(event);
  SyndicatedVenuesGalleryObject.supportSwitcher(event);
});
